import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../../supabaseClient.jsx";
import { Clock, DollarSign } from "lucide-react";
import { Button, Card } from "../../../../design-system/index.js";
import { cn } from "../../../../design-system/index.js";

const imageCache = new Map();

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value ?? 0);

const getImageUrl = (path) => {
  if (!path) return "/assets/default-pacote.webp";
  if (path.startsWith("http")) return path;
  if (imageCache.has(path)) return imageCache.get(path);
  const { data } = supabase.storage.from("Fundo-pacote-horas").getPublicUrl(path);
  imageCache.set(path, data.publicUrl);
  return data.publicUrl;
};

export default function PackagesSection() {
  const [pacotes, setPacotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPacotes = async () => {
      try {
        const { data, error } = await supabase
          .from("produtos")
          .select("*")
          .eq("tipo", "pacote_horas")
          .eq("ativo", true)
          .order("valor", { ascending: true });
        if (error) throw error;
        setPacotes(data || []);
      } catch (error) {
        console.error("Erro ao carregar pacotes:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPacotes();
  }, []);

  if (loading) {
    return (
      <section id="pacotes" className="bg-[var(--color-warm-50)] py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 animate-pulse">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-64 rounded-2xl bg-[var(--color-warm-200)]" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (pacotes.length === 0) {
    return (
      <section id="pacotes" className="bg-[var(--color-warm-50)] py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 text-center">
          <p className="text-[var(--color-warm-400)] text-sm">
            No momento, não temos pacotes disponíveis. Por favor, volte em breve!
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="pacotes" className="bg-[var(--color-warm-50)] py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="text-center mb-10">
          <h2 className={cn(
            "font-heading text-3xl sm:text-4xl font-bold",
            "text-[var(--color-warm-900)]"
          )}>
            Nossos Pacotes Disponíveis
          </h2>
          <p className={cn(
            "mt-3 text-[var(--color-warm-500)] text-sm sm:text-base",
            "max-w-md mx-auto"
          )}>
            Escolha o pacote ideal para sua família e comece hoje mesmo.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {pacotes.map((pacote) => (
            <Card
              key={pacote.id}
              className={cn(
                "relative rounded-2xl overflow-hidden flex flex-col justify-end",
                "h-72 cursor-pointer hover:shadow-xl transition-shadow duration-200"
              )}
              onClick={() => navigate("/login")}
            >
              {/* Imagem de fundo */}
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${getImageUrl(pacote.imagem)})` }}
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />

              {/* Conteúdo */}
              <div className="relative z-10 p-4 text-white text-center flex flex-col gap-1">
                <h3 className="text-base font-bold leading-tight">{pacote.nome}</h3>
                <p className="flex items-center justify-center gap-1 text-sm">
                  <Clock size={14} /><strong>{pacote.horas} horas</strong>
                </p>
                <p className="flex items-center justify-center gap-1 text-sm">
                  <DollarSign size={14} /><strong>{formatCurrency(pacote.valor)}/h</strong>
                </p>
                <p className="text-sm font-semibold text-white/90">
                  Total: <strong>{formatCurrency(pacote.horas * pacote.valor)}</strong>
                </p>

                <Button
                  variant="default"
                  size="sm"
                  className="mt-2 mx-auto rounded-xl px-5 py-2 font-semibold hover:brightness-110"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/login");
                  }}
                >
                  Venha conferir
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}